import { Type, type TSchema } from '@sinclair/typebox'
import { t } from 'elysia/type-system'
import {
  isEmail,
  isPersonName,
  isPhone,
  isPostalCode,
  PASSWORD_MAXIMUM,
  PASSWORD_MINIMUM,
} from '@porotutu/http/vocabulary'
import type { FieldError } from './failure'

const ENUM_MESSAGES = { required: 'errors.required', union: 'errors.not_a_known_value' }

export const choice = <const Values extends readonly [string, ...string[]]>(values: Values) =>
  t.UnionEnum(values, { default: undefined, messages: ENUM_MESSAGES })

export const uuidSchema = t.String({
  format: 'uuid',
  messages: { required: 'errors.must_be_chosen', format: 'errors.must_be_chosen' },
})

export const passwordSchema = t.String({
  minLength: PASSWORD_MINIMUM,
  maxLength: PASSWORD_MAXIMUM,
  messages: {
    required: 'errors.required',
    minLength: 'errors.password_too_short',
    maxLength: 'errors.password_too_long',
  },
})

export const requiredText = (max: number) =>
  t.String({
    minLength: 1,
    maxLength: max,
    pattern: '\\S',
    messages: {
      required: 'errors.required',
      minLength: 'errors.required',
      maxLength: 'errors.too_long',
      pattern: 'errors.required',
    },
  })

export const emailText = (max: number) =>
  t.String({
    format: 'email',
    maxLength: max,
    messages: {
      required: 'errors.required',
      format: 'errors.not_an_email',
      maxLength: 'errors.too_long',
    },
  })

export const optionalText = (max: number) =>
  t.String({ maxLength: max, messages: { maxLength: 'errors.too_long' } })

export const nullable = <S extends TSchema>(schema: S) =>
  t.Union([schema, t.Null()], { nullable: true })

export const nullableText = (max: number) => t.Optional(nullable(optionalText(max)))

export const daySchema = t.String({
  format: 'date',
  messages: { required: 'errors.required', format: 'errors.not_a_date' },
})

export const amountSchema = t.String({
  pattern: '^\\d+(\\.\\d{1,2})?$',
  messages: { required: 'errors.required', pattern: 'errors.not_an_amount' },
})

export const countryCodeSchema = t.String({
  minLength: 2,
  maxLength: 2,
  pattern: '^[A-Za-z]{2}$',
  messages: {
    required: 'errors.required',
    minLength: 'errors.not_a_country_code',
    maxLength: 'errors.not_a_country_code',
    pattern: 'errors.not_a_country_code',
  },
})

export const measureSchema = t.String({
  pattern: '^\\d+(\\.\\d{1,3})?$',
  messages: { required: 'errors.required', pattern: 'errors.not_an_amount' },
})

export const coordinate = (bound: number) =>
  t.Optional(
    nullable(
      Type.Number({
        minimum: -bound,
        maximum: bound,
        messages: { minimum: 'errors.too_small', maximum: 'errors.too_large' },
      }),
    ),
  )

export const trimmed = (value: string | null | undefined) => (value ?? '').trim()

export const refusalAt = (path: string, message: string): FieldError => ({ path, message })

export const confirmed =
  <T>(field: keyof T & string, against: keyof T & string) =>
  (values: T): FieldError[] =>
    values[field] === values[against] ? [] : [refusalAt(field, 'errors.password_mismatch')]

export function textRefusals(
  values: Record<string, unknown>,
  rules: Record<string, (value: string) => string | undefined>,
  prefix = '',
): FieldError[] {
  const failures: FieldError[] = []

  for (const [field, rule] of Object.entries(rules)) {
    const value = trimmed(values[field] as string | null | undefined)

    if (value === '') continue

    const refused = rule(value)

    if (refused !== undefined) {
      failures.push(refusalAt(prefix === '' ? field : `${prefix}/${field}`, refused))
    }
  }

  return failures
}

export const personNameRefusal = (value: string) =>
  isPersonName(value) ? undefined : 'errors.not_a_name'

export const emailRefusal = (value: string) => (isEmail(value) ? undefined : 'errors.not_an_email')

export const phoneRefusal = (value: string) => (isPhone(value) ? undefined : 'errors.not_a_phone')

export const postalCodeRefusal = (value: string) =>
  isPostalCode(value) ? undefined : 'errors.not_a_postal_code'
